import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  AudienceMember,
  ActivityLevel,
  WindowBehavior,
  useLazyGetAudienceMembersQuery,
} from '../../redux/adminApiSlice';
import { audienceAvatarUrl, audienceLocationText } from './audienceDisplay';
import PushChip from './AudiencePushChip';

export interface BucketSelection {
  axis: 'active' | 'signup';
  window: string;
  label: string;
}

function fmtRelative(d: string | null | undefined): string {
  if (!d) return 'Never';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return 'Never';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function fmtAge(days: number): string {
  if (days < 1) {
    const hours = Math.round(days * 24);
    return hours <= 1 ? 'today' : `${hours}h old`;
  }
  if (days < 30) return `${Math.round(days)}d old`;
  if (days < 365) return `${Math.round(days / 30)}mo old`;
  return `${(days / 365).toFixed(1)}y old`;
}

const LEVEL_META: Record<ActivityLevel, { label: string; color: string }> = {
  power: { label: 'Power', color: '#7b1fa2' },
  regular: { label: 'Regular', color: '#2e7d32' },
  occasional: { label: 'Occasional', color: '#0288d1' },
  dormant: { label: 'Dormant', color: '#9e9e9e' },
  new: { label: 'New', color: '#ed6c02' },
};

function ActivityLevelChip({ level }: { level: ActivityLevel }) {
  const meta = LEVEL_META[level] || LEVEL_META.occasional;
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        bgcolor: `${meta.color}22`,
        color: meta.color,
        fontWeight: 600,
        height: 20,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

const BEHAVIOR_META: Record<WindowBehavior, { label: string; color: string }> = {
  posted: { label: 'Posted', color: '#2e7d32' },
  messaged: { label: 'Messaged', color: '#0288d1' },
  lurked: { label: 'Lurked', color: '#9e9e9e' },
};

function BehaviorChip({
  behavior,
  postsInWindow,
  messagesInWindow,
}: {
  behavior: WindowBehavior;
  postsInWindow: number;
  messagesInWindow: number;
}) {
  const meta = BEHAVIOR_META[behavior];
  const detail =
    behavior === 'lurked'
      ? 'Signed on but did not post or message in this window'
      : [
          postsInWindow > 0 ? `${postsInWindow} post${postsInWindow === 1 ? '' : 's'}` : '',
          messagesInWindow > 0
            ? `${messagesInWindow} message${messagesInWindow === 1 ? '' : 's'}`
            : '',
        ]
          .filter(Boolean)
          .join(' · ');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Tooltip title={detail}>
        <Chip
          label={meta.label}
          size="small"
          sx={{
            bgcolor: `${meta.color}22`,
            color: meta.color,
            fontWeight: 600,
            height: 20,
            alignSelf: 'flex-start',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Tooltip>
      {behavior !== 'lurked' ? (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * The people behind a stat. Lists who they are, how long ago they signed up,
 * how active they generally are, and what they've posted. Row click bubbles the
 * user up so the parent can open the full UserModal.
 */
const BucketMembersDialog: React.FC<{
  selection: BucketSelection | null;
  includeExcluded: boolean;
  onClose: () => void;
  onOpenUser: (member: AudienceMember) => void;
}> = ({ selection, includeExcluded, onClose, onOpenUser }) => {
  const [fetchMembers, result] = useLazyGetAudienceMembersQuery();
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!selection) return;
    const verb = selection.axis === 'active' ? 'Active' : 'Signed up';
    setTitle(`${verb} — ${selection.label}`);
    void fetchMembers({
      axis: selection.axis,
      window: selection.window,
      includeExcluded,
      limit: 500,
    });
  }, [selection, includeExcluded, fetchMembers]);

  const members = result.data?.members || [];
  const total = result.data?.total ?? members.length;

  const breakdown = members.reduce(
    (acc, m) => {
      acc[m.windowBehavior] += 1;
      return acc;
    },
    { posted: 0, messaged: 0, lurked: 0 } as Record<WindowBehavior, number>
  );
  const showBreakdown = selection?.axis === 'active' && members.length > 0;

  return (
    <Dialog open={!!selection} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {title}
          {!result.isFetching ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {total.toLocaleString()} {total === 1 ? 'person' : 'people'}
              {members.length < total ? ` (showing first ${members.length})` : ''}
            </Typography>
          ) : null}
          {showBreakdown ? (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
              <Chip
                size="small"
                label={`${breakdown.posted} posted`}
                sx={{ height: 22, bgcolor: '#2e7d3222', color: '#2e7d32', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`${breakdown.messaged} messaged`}
                sx={{ height: 22, bgcolor: '#0288d122', color: '#0288d1', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`${breakdown.lurked} lurked`}
                sx={{ height: 22, bgcolor: '#9e9e9e22', color: '#9e9e9e', fontWeight: 600 }}
              />
            </Box>
          ) : null}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {result.isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : members.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            No one in this bucket.
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Did (in window)</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell align="center">Push</TableCell>
                <TableCell>Last active</TableCell>
                <TableCell>Signed up</TableCell>
                <TableCell>Where</TableCell>
                <TableCell align="right">Posts</TableCell>
                <TableCell>Last post</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow
                  key={m._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onOpenUser(m)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar
                          src={audienceAvatarUrl(m)}
                          alt={m.displayName}
                          sx={{ width: 36, height: 36, fontSize: 15 }}
                        >
                          {(m.displayName || m.username || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        {m.isOnline ? (
                          <Tooltip title="Online now">
                            <Box
                              sx={{
                                position: 'absolute',
                                right: -1,
                                bottom: -1,
                                width: 11,
                                height: 11,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                border: '2px solid',
                                borderColor: 'background.paper',
                              }}
                            />
                          </Tooltip>
                        ) : null}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {m.displayName}
                          {m.isMockUser ? (
                            <Chip label="mock" size="small" sx={{ ml: 0.5, height: 18 }} />
                          ) : null}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          @{m.username || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <BehaviorChip
                      behavior={m.windowBehavior}
                      postsInWindow={m.postsInWindow}
                      messagesInWindow={m.messagesInWindow}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <ActivityLevelChip level={m.activityLevel} />
                      <Typography variant="caption" color="text.secondary">
                        {m.postsPerWeek > 0 ? `${m.postsPerWeek}/wk` : 'no posts'} · {fmtAge(m.accountAgeDays)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <PushChip pushable={m.pushable} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{fmtRelative(m.lastActive)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{fmtRelative(m.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{audienceLocationText(m)}</Typography>
                  </TableCell>
                  <TableCell align="right">{m.postCount}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {fmtRelative(m.lastPostAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BucketMembersDialog;
